import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllDriveLabelsForDriveSearchQuery } from '../../../../api/users-api';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { BulkImportType, UserRole } from '../../../../../types';
import SharedTransferList from '../../../../shared/shared-transfer-list';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import ExternalUsersInput from '../../../../shared/external-users-input';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import SourceTypeSelector from './source-type-selector';
import { getSourceTypeOptions } from './source-type-options';
import { useLightestOutline } from './use-lightest-outline';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface StepSourcesProps {
  control: Control<BulkOpsFormValues>;
  setValue: UseFormSetValue<BulkOpsFormValues>;
  watch: UseFormWatch<BulkOpsFormValues>;
  errors: FieldErrors<BulkOpsFormValues>;
  trigger: UseFormTrigger<BulkOpsFormValues>;
  selectedBulkImportType: BulkImportType | null;
  domains: string[];
  roles: UserRole[];
  clearErrors: UseFormClearErrors<BulkOpsFormValues>;
  isNewUi?: boolean;
}

export default function StepSources({
  control,
  setValue,
  watch,
  errors,
  trigger,
  selectedBulkImportType,
  domains,
  roles,
  clearErrors,
  isNewUi = false,
}: StepSourcesProps) {
  const sources = watch('sources') || [];
  const sourceType = watch('sourceType');
  const allSource = watch('allSource');
  const path = watch('path');
  const { allDomains } = useUserSettings();
  const lightestOutline = useLightestOutline();
  const sourceTypeOptions = getSourceTypeOptions(selectedBulkImportType!.name);

  const { data: driveLabelsResponse, isLoading: driveLabelsLoading } = useQuery(
    {
      ...getAllDriveLabelsForDriveSearchQuery(),
    }
  );

  const driveLabels = useMemo(() => {
    const labelArray = (driveLabelsResponse?.labels ?? []).map(
      (entry) => entry.label
    );
    return labelArray
      .filter((l) => l.lifecycle?.state === 'PUBLISHED')
      .map((l) => ({
        id: l.id ?? '',
        title: l.properties?.title ?? l.id ?? '',
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [driveLabelsResponse]);

  const getDomain = (email: string) =>
    (email.split('@')[1] ?? '').toLowerCase();

  const internalUsers =
    sources?.filter(
      (source) =>
        !source.includes('@') || allDomains.includes(getDomain(source))
    ) ?? [];

  const externalUsers =
    sources?.filter(
      (source) =>
        source.includes('@') && !allDomains.includes(getDomain(source))
    ) ?? [];

  function addSelection(emails: string[]) {
    const uniqueEmails = Array.from(new Set([...sources!, ...emails]));
    setValue('sources', uniqueEmails);
    clearErrors();
    if (uniqueEmails.length === 0) {
      trigger('sources');
    }
  }

  function removeSelection(emails: string[]) {
    const newList = sources!.filter((userEmail) => !emails.includes(userEmail));
    setValue('sources', newList);
  }

  const handleAddAllChecked = (value: boolean) => {
    setValue('allSource', value);
    if (value) {
      setValue('sources', []);
      clearErrors();
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      <Stack gap={2} sx={{ minHeight: '100%' }}>
        {isNewUi && (
          <FormSectionHeader
            descriptionVariant="body2"
            description={
              selectedBulkImportType!.name === 'sharedContactImport'
                ? 'Optionally provide users who will have their contacts added to the shared contacts spreadsheet.'
                : 'Add at least one item this bulk operation will apply to.'
            }
          >
            Select Sources
          </FormSectionHeader>
        )}
        {isNewUi && <Divider sx={{ borderColor: lightestOutline }} />}
        {path === 'guideMe' &&
          selectedBulkImportType?.name === 'labelDriveDocs' && (
            <Stack gap={1} sx={{ width: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Optional Filters
              </Typography>
              <Stack
                flexDirection={'row'}
                gap={2}
                width={'100%'}
                alignItems={'flex-start'}
              >
                <Controller
                  name="existingLabelFilter"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Existing label equals"
                      variant="outlined"
                      fullWidth
                      value={field.value || ''}
                      sx={{ flex: 1 }}
                      disabled={driveLabelsLoading}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {driveLabels.map((label) => (
                        <MenuItem key={label.id} value={label.id}>
                          {label.title}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  name="visibilityFilter"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Visibility"
                      variant="outlined"
                      fullWidth
                      value={field.value || ''}
                      sx={{ flex: 1 }}
                      helperText="Note: Visibility criteria is only evaluated on documents owned by the targeted users."
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      <MenuItem value="Private">Private</MenuItem>
                      <MenuItem value="Shared">Shared</MenuItem>
                      <MenuItem value="Domain">Domain</MenuItem>
                      <MenuItem value="Public">Public</MenuItem>
                    </TextField>
                  )}
                />
              </Stack>
            </Stack>
          )}
        {!isNewUi &&
          (selectedBulkImportType!.name === 'sharedContactImport' ? (
            <Typography sx={{ mb: 1 }}>
              Optionally provide users who will have their contacts added to the
              shared contacts spreadsheet.
            </Typography>
          ) : (
            <Typography>
              Please select at least one source to apply your bulk operation
              too.
            </Typography>
          ))}
        <Controller
          name={`sourceType`}
          control={control}
          render={({ field: props }) =>
            isNewUi ? (
              <SourceTypeSelector
                options={sourceTypeOptions}
                value={props.value}
                onChange={props.onChange}
                hasSources={sources!.length > 0}
                allSource={allSource === true}
              />
            ) : (
              <Stack
                flexDirection={'row'}
                gap={4}
                alignItems={'center'}
                justifyContent="center"
              >
                <FormControl>
                  <FormLabel>
                    {sources!.length > 0
                      ? 'Source Type (Only one source type is allowed)'
                      : 'Source Type'}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="radio-buttons-group-label"
                    defaultValue={undefined}
                    name="radio-buttons-group"
                    value={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.value);
                    }}
                    row
                  >
                    {sourceTypeOptions.map((option) => (
                      <Tooltip
                        key={option.value}
                        arrow
                        title={
                          option.notApplicable
                            ? 'Not applicable to this operation type.'
                            : ''
                        }
                      >
                        <FormControlLabel
                          value={option.value}
                          control={<Radio />}
                          label={option.label}
                          disabled={
                            sources!.length > 0 ||
                            option.notApplicable ||
                            allSource === true
                          }
                        />
                      </Tooltip>
                    ))}
                  </RadioGroup>
                </FormControl>
              </Stack>
            )
          }
        />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
          }}
        >
          <Controller
            name={'sources'}
            control={control}
            render={() => (
              <SharedTransferList
                sources={internalUsers}
                addSelection={addSelection}
                removeSelection={removeSelection}
                handleAddAllChecked={handleAddAllChecked}
                errors={!!errors.sources}
                helperText={errors?.sources?.message || ' '}
                allSource={allSource}
                domains={domains || []}
                sourceType={sourceType || ''}
                hasSelectAllOption={true}
                roles={roles || []}
                newUi={isNewUi}
              />
            )}
          />
        </Box>
        {sourceType === 'User' &&
          selectedBulkImportType?.name === 'groupRemoval' && (
            <ExternalUsersInput
              value={externalUsers}
              onAdd={(email) => {
                const current = sources ?? [];
                setValue('sources', [...current, email], {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              onRemove={(email) => {
                const current = sources ?? [];
                setValue(
                  'sources',
                  current.filter((source) => source !== email),
                  { shouldDirty: true, shouldValidate: true }
                );
              }}
            />
          )}
      </Stack>
    </Box>
  );
}
